import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { AuthStore } from '../../services/auth-store.service';

export const AUTH_REQUEST_RETRIED = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (request, next) => {
   const store = inject(AuthStore);
   const injector = inject(Injector);
   const isPlatformApi = request.url.startsWith(environment.apiBaseUrl);
   const isPublicAuthEndpoint = isPublicAppAuthEndpoint(request.url);
   const isConsumerProtectedEndpoint =
      request.url.includes('/AppUserProfile/') ||
      request.url.includes('/Loyalty/') ||
      request.url.includes('/AppAuth/password/change');

   const shouldAttachToken = isPlatformApi && !isPublicAuthEndpoint && isConsumerProtectedEndpoint;

   const authRequest =
      shouldAttachToken && store.token
         ? request.clone({
              setHeaders: { Authorization: `Bearer ${store.token}` },
           })
         : request;

   return next(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
         const alreadyRetried = request.context.get(AUTH_REQUEST_RETRIED);
         const canRefresh =
            error.status === 401 &&
            !isPublicAuthEndpoint &&
            shouldAttachToken &&
            !!store.token &&
            !alreadyRetried;

         if (!canRefresh) {
            return throwError(() => error);
         }

         const authService = injector.get(AuthService);

         return authService.refreshAccessToken().pipe(
            switchMap((refreshed) => {
               if (!refreshed || !store.token) {
                  return throwError(() => error);
               }

               return next(
                  request.clone({
                     context: request.context.set(AUTH_REQUEST_RETRIED, true),
                     setHeaders: { Authorization: `Bearer ${store.token}` },
                  }),
               );
            }),
         );
      }),
   );
};

function isPublicAppAuthEndpoint(url: string): boolean {
   return url.includes('/AppAuth/') && !url.includes('/AppAuth/password/change');
}
